import {
  Button,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateForEdit } from "../utils/dateUtils";

const TransactionTable = ({
  title,
  transactionType,
  fetchFunction,
  deleteFunction,
  editPath,
  addPath,
  filterableFields = [],
  columns = [],
  defaultSortColumn,
  sortOrder,
}) => {
  const [transactions, setTransactions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [orderBy, setOrderBy] = useState(defaultSortColumn || "date");
  const [order, setOrder] = useState(sortOrder);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const data = await fetchFunction(transactionType);
        const formattedAndSortedData = data
          .map((entry) => ({
            ...entry,
            date: entry.date ? formatDateForEdit(entry.date) : null,
            start_date: entry.start_date
              ? formatDateForEdit(entry.start_date)
              : null,
            next_due_date: entry.next_due_date
              ? formatDateForEdit(entry.next_due_date)
              : null,
          }))
          .sort(
            (a, b) =>
              new Date(b.date || b.start_date) -
              new Date(a.date || a.start_date)
          );

        setTransactions(formattedAndSortedData);
      } catch (error) {
        console.error(error);
        alert(`Error fetching ${transactionType}s`);
      }
    };

    fetchTransactions();
  }, [transactionType, fetchFunction]);

  const handleDelete = async (id) => {
    try {
      await deleteFunction(id);
      setTransactions(transactions.filter((item) => item.id !== id));
    } catch (error) {
      console.error(error);
      alert("Failed to delete transaction.");
    }
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const getNestedValue = (obj, path) => {
    if (typeof path !== "string") return obj[path];
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  const handleFilterChange = (id, value) => {
    setSelectedFilters((prev) => ({ ...prev, [id]: value }));
  };

  const filterOptions = {};

  transactions.forEach((transaction) => {
    filterableFields.forEach((field) => {
      const value = getNestedValue(transaction, field);
      if (value) {
        if (!filterOptions[field]) filterOptions[field] = new Set();
        filterOptions[field].add(value);
      }
    });
  });

  const filteredTransactions = transactions.filter((transaction) => {
    const matchesFilters = Object.keys(selectedFilters).every(
      (key) =>
        selectedFilters[key] === "ALL" ||
        getNestedValue(transaction, key) === selectedFilters[key]
    );

    const matchesSearch = transaction.vendor
      ? transaction.vendor.toLowerCase().includes(searchQuery.toLowerCase())
      : false;

    return matchesFilters && matchesSearch;
  });

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    const aValue = getNestedValue(a, orderBy);
    const bValue = getNestedValue(b, orderBy);

    if (["date", "start_date", "next_due_date", "end_date"].includes(orderBy)) {
      return order === "asc"
        ? new Date(aValue) - new Date(bValue)
        : new Date(bValue) - new Date(aValue);
    }

    if (aValue === undefined || aValue === null) return 1;
    if (bValue === undefined || bValue === null) return -1;

    const aNum = parseFloat(aValue);
    const bNum = parseFloat(bValue);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      return order === "asc" ? aNum - bNum : bNum - aNum;
    }

    return order === "asc"
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });
  return (
    <TableContainer
      component={Paper}
      sx={{ maxWidth: "90%", margin: "auto", mt: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
        {title}
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate(addPath)}
        sx={{ mb: 2, ml: 1 }}
      >
        Add {transactionType}
      </Button>

      <br></br>
      <TextField
        label="Search by Vendor"
        variant="outlined"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2, ml: 1, width: "200px" }}
      />

      {filterableFields.length > 0 && (
        <>
          <br></br>
          {filterableFields.map(({ id, label }) => (
            <React.Fragment key={id}>
              <Select
                key={id}
                value={selectedFilters[id] || "ALL"}
                onChange={(e) => handleFilterChange(id, e.target.value)}
                sx={{ ml: 1, width: "200px", height: "40px", mb: 1 }}
              >
                <MenuItem value="ALL">All {label}</MenuItem>
                {[...new Set(transactions.map((t) => t[id]))].map((value) => (
                  <MenuItem key={`${id}-${value}`} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <br></br>
            </React.Fragment>
          ))}
        </>
      )}

      <Table>
        <TableHead>
          <TableRow>
            {columns.map(({ id, label, sortable }) => (
              <TableCell key={id} sx={{ padding: "4px 8px" }}>
                {sortable ? (
                  <TableSortLabel
                    active={orderBy === id}
                    direction={order}
                    onClick={() => handleSort(id)}
                  >
                    <strong>{label}</strong>
                  </TableSortLabel>
                ) : (
                  <strong>{label}</strong>
                )}
              </TableCell>
            ))}
            <TableCell>
              <strong>Actions</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedTransactions.map((transaction) => (
            <TableRow
              key={transaction.id}
              sx={{
                height: "30px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.03)",
                },
              }}
            >
              {columns.map(({ id, format }) => {
                const value = getNestedValue(transaction, id);
                return (
                  <TableCell key={id} sx={{ padding: "4px 8px" }}>
                    {format ? format(value) : value || ""}
                  </TableCell>
                );
              })}
              <TableCell sx={{ padding: "4px 8px" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ height: "25px" }}
                  onClick={() => navigate(`${editPath}/${transaction.id}`)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  sx={{ height: "25px", ml: 1 }}
                  onClick={() => handleDelete(transaction.id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TransactionTable;
